import React, { useCallback, useEffect, useState } from 'react';
import { RouteComponentProps } from 'react-router-dom';
import { Box, Flex, Text } from 'rebass';
import { HazardSurveyResult } from '../../components/WaterHazard/HazardSurveyResult';
import {
  getHazardSurvey,
  getHazardSurveyAttachments,
  HazardSurveyAttachment,
  HazardSurveyData,
  HazardSurveySubmitPayload,
  submitHazardSurvey,
} from '../../services/hazardSurvey';

type Props = RouteComponentProps<{ token: string }>;

const COMPLETED_STATUSES = ['IN_REVIEW', 'ACCEPTED'];

const HazardSurveyPublicPage: React.FC<Props> = ({ match }) => {
  const { token } = match.params;

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [data, setData] = useState<HazardSurveyData | null>(null);
  const [submitted, setSubmitted] = useState(false);
  const [attachments, setAttachments] = useState<HazardSurveyAttachment[]>([]);

  const refreshAttachments = useCallback(() => {
    getHazardSurveyAttachments(token)
      .then(setAttachments)
      .catch(() => {});
  }, [token]);

  useEffect(() => {
    getHazardSurvey(token)
      .then((d) => {
        setData(d);
        refreshAttachments();
      })
      .catch((e) => setError(e.message ?? 'Failed to load survey'))
      .finally(() => setLoading(false));
  }, [token, refreshAttachments]);

  const handleSubmit = async (values: HazardSurveySubmitPayload) => {
    await submitHazardSurvey(token, values);
    setSubmitted(true);
  };

  if (loading) {
    return (
      <Flex justifyContent="center" alignItems="center" minHeight="100vh">
        <Text>Loading survey…</Text>
      </Flex>
    );
  }

  if (error) {
    return (
      <Flex justifyContent="center" alignItems="center" minHeight="100vh">
        <Box textAlign="center">
          <Text fontSize={3} mb={2}>
            Unable to load survey
          </Text>
          <Text color="gray.500">{error}</Text>
        </Box>
      </Flex>
    );
  }

  if (!data) return null;

  const isReopened =
    data.survey.status === 'IN_PROGRESS' &&
    data.survey.items.some((i) => i.answer !== null);

  const isAlreadyCompleted =
    submitted || COMPLETED_STATUSES.includes(data.status ?? '');

  if (isAlreadyCompleted) {
    return (
      <Flex justifyContent="center" alignItems="center" minHeight="100vh">
        <Box
          bg="white"
          p={5}
          sx={{
            boxShadow: 'feature',
            borderRadius: 4,
            maxWidth: 500,
            textAlign: 'center',
          }}
        >
          <Text fontSize={4} mb={3}>
            Survey Complete
          </Text>
          <Text color="gray.600">
            {submitted
              ? 'Thank you! Your survey has been submitted successfully.'
              : 'This survey has already been completed. Thank you for your response.'}
          </Text>
        </Box>
      </Flex>
    );
  }

  const { survey } = data;

  const initialValues = isReopened
    ? {
        submitterName: survey.submitterName ?? undefined,
        submitterPhone: survey.submitterPhone ?? undefined,
        submitterEmail: survey.submitterEmail ?? undefined,
        hazards: Object.fromEntries(
          survey.items
            .filter((i) => i.answer !== null)
            .map((i) => [i.waterHazard.id, i.answer!])
        ),
        protectedByHazard: Object.fromEntries(
          survey.items.map((i) => [
            i.waterHazard.id,
            i.isProtected === 'YES' ||
            i.isProtected === 'NO' ||
            i.isProtected === 'UNKNOWN'
              ? i.isProtected
              : null,
          ])
        ),
        backflowAssemblyByHazard: Object.fromEntries(
          survey.items.map((i) => [
            i.waterHazard.id,
            i.backflowAssemblyId != null ? String(i.backflowAssemblyId) : null,
          ])
        ),
        otherHazards: survey.otherHazardsAnswer ?? undefined,
        comments: survey.comments ?? undefined,
      }
    : undefined;

  const template = {
    instructions: survey.instructions ?? '',
    otherHazardsQuestion: survey.otherHazardsQuestion,
    protectedQuestion: survey.protectedQuestion,
    hazardIds: survey.items.map((item) => item.waterHazard.id),
  };

  const hazards = survey.items.map((item) => ({
    id: item.waterHazard.id,
    name: item.waterHazard.name,
    description: item.waterHazard.description ?? '',
    hazardLevel: (item.waterHazard.hazardLevel ?? 'LOW') as any,
  }));

  const branding = data.branding;

  const knownHazards = (survey.waterService.waterServiceHazards ?? []).map(
    (h) => ({
      id: h.waterHazard.id,
      name: h.waterHazard.name,
      hazardLevel: h.waterHazard.hazardLevel,
      isProtected: h.isProtected,
      backflowAssemblyId: h.backflowAssemblyId
        ? String(h.backflowAssemblyId)
        : null,
    })
  );

  return (
    <Box
      sx={{
        minHeight: '100vh',
        background: 'linear-gradient(0deg, #fff, #fff, #f1f1f1)',
      }}
      px={[2, 4]}
      pt={0}
      pb={4}
    >
      <HazardSurveyResult
        template={template}
        hazards={hazards}
        mode="fill"
        initialValues={initialValues}
        knownHazards={knownHazards}
        waterService={survey.waterService}
        onSubmit={handleSubmit}
        surveyToken={token}
        attachments={attachments}
        onAttachmentUploaded={refreshAttachments}
        branding={branding}
        pt={4}
        mb={3}
      />
    </Box>
  );
};

export default HazardSurveyPublicPage;
